import mixpanel from 'mixpanel-browser'

import { MIXPANEL_PROJECT_TOKEN_PROD, MIXPANEL_PROJECT_TOKEN_TEST, MIXPANEL_TRACKING_PROXY_HOST } from '@tipi/shared'

import { isLocalEnvironment, isTestEnvironment } from '../environment.ts'
import { getAttributionParameters, persistAttributionToCookie } from '../lib/urlParams.ts'

let initialized = false
let measurementEnabled = false

export function isMeasurementEnabled(): boolean {
    return measurementEnabled && initialized
}

export function applyMeasurementConsent(enabled: boolean): void {
    measurementEnabled = enabled
    if (enabled) {
        initLandingTracking()
    }
}

export function initLandingTracking(): void {
    if (initialized || typeof window === 'undefined') return
    initialized = true

    const projectToken = isTestEnvironment ? MIXPANEL_PROJECT_TOKEN_TEST : MIXPANEL_PROJECT_TOKEN_PROD

    mixpanel.init(projectToken, {
        api_host: MIXPANEL_TRACKING_PROXY_HOST,
        debug: isLocalEnvironment,
        flags: true,
        record_sessions_percent: 50,
        record_heatmap_data: true,
        record_mask_text_selector: 'input, textarea',
    })

    persistAttributionToCookie()

    mixpanel.register_once(getAttributionParameters())
}

type TrackProperties = Record<string, unknown>
type TrackOptions = {
    send_immediately?: boolean
    transport?: 'xhr' | 'sendBeacon'
}

export function track(
    eventName: string,
    properties?: TrackProperties,
    optionsOrCallback?: TrackOptions | (() => void),
    callback?: () => void,
): void {
    if (!isMeasurementEnabled()) {
        if (typeof optionsOrCallback === 'function') optionsOrCallback()
        else callback?.()
        return
    }

    if (typeof optionsOrCallback === 'function') {
        mixpanel.track(eventName, properties, optionsOrCallback)
        return
    }

    mixpanel.track(eventName, properties, optionsOrCallback, callback)
}

export function trackPageview(properties?: TrackProperties): void {
    if (!isMeasurementEnabled()) return
    mixpanel.track_pageview(properties)
}

export async function getFeatureFlagVariant(
    flagId: string,
    fallback: { key: string; value: undefined },
): Promise<{ key: string; value: undefined }> {
    if (!isMeasurementEnabled()) return fallback
    return mixpanel.flags.get_variant(flagId, fallback)
}
